import { Notification } from '@dreryk/egabinet-user-inbox-frontend-api';

import { dateTimeMinuteFormatter } from '@dreryk/react-common';

import { patientPaths, visitPaths } from '~libs.desktop.shared.paths';

type NotificationMetadataCode =
   | 'patientId'
   | 'patientFirstName'
   | 'patientLastName'
   | 'requestStatus'
   | 'resourceId'
   | 'resourceName'
   | 'resourceType'
   | 'accessValidTo';

type NotificationTarget =
   | { type: 'medicalData'; patientId: number; category?: string; entryId: number }
   | { type: 'medicalDocument'; patientId: number; documentId: number }
   | { type: 'visit'; patientId: number; visitId: number };

const MEDICAL_DATA_CATEGORY_SLUG_BY_RESOURCE_NAME: Record<string, string> = {
   Uczulenia: 'uczulenia',
   'Wyniki badań analitycznych': 'wyniki-badan',
   Antropometria: 'antropometria',
   'Choroby przewlekłe': 'choroby-przewlekle',
   Rozpoznania: 'rozpoznania',
   'Wywiad ginekologiczny': 'wywiad-ginekologiczny',
   Hospitalizacje: 'hospitalizacje',
   'Konsultacje medyczne': 'konsultacje',
   'Wyniki badań obrazowych': 'badania-obrazowe',
   Inne: 'inne',
   Skierowanie: 'skierowania',
   Rehabilitacje: 'rehabilitacja',
   'Czynniki ryzyka': 'czynniki-ryzyka',
   'Zabiegi i operacje': 'leczenie',
};

const getMetadataValue = (notification: Notification, code: NotificationMetadataCode): string | undefined =>
   notification.metadata?.find((entry) => entry.code === code)?.value;

const getPatientName = (notification: Notification): string =>
   [getMetadataValue(notification, 'patientFirstName'), getMetadataValue(notification, 'patientLastName')]
      .filter(Boolean)
      .join(' ');

const getNotificationTarget = (notification: Notification): NotificationTarget | undefined => {
   const resourceType = getMetadataValue(notification, 'resourceType');
   const resourceId = getMetadataValue(notification, 'resourceId');
   const patientIdValue = getMetadataValue(notification, 'patientId');

   if (resourceId === undefined || patientIdValue === undefined) {
      return undefined;
   }

   const patientId = Number(patientIdValue);
   const entryId = Number(resourceId);

   if (resourceType === 'MEDICAL_INFORMATION') {
      const resourceName = getMetadataValue(notification, 'resourceName');
      const category = resourceName ? MEDICAL_DATA_CATEGORY_SLUG_BY_RESOURCE_NAME[resourceName] : undefined;
      return { type: 'medicalData', patientId: patientId, category: category, entryId: entryId };
   }

   if (resourceType === 'MEDICAL_DOCUMENT' || resourceType === 'PATIENT_MEDICAL_DOCUMENTATION') {
      return { type: 'medicalDocument', patientId: patientId, documentId: entryId };
   }

   if (resourceType === 'VISIT') {
      return { type: 'visit', patientId: patientId, visitId: entryId };
   }

   return undefined;
};

export const getNotificationResourceName = (notification: Notification): string | undefined => {
   const resourceName = getMetadataValue(notification, 'resourceName');

   if (resourceName === undefined) {
      return undefined;
   }

   const resourceType = getMetadataValue(notification, 'resourceType');

   return resourceType === 'MEDICAL_INFORMATION' ? `Dane medyczne / ${resourceName}` : resourceName;
};

export const buildNotificationMessage = (notification: Notification): string => {
   const patientName = getPatientName(notification);
   const requestStatus = getMetadataValue(notification, 'requestStatus');

   switch (requestStatus) {
      case 'ACCEPTED': {
         const accessValidTo = getMetadataValue(notification, 'accessValidTo');
         const accessValidToText = accessValidTo
            ? ` Dostęp do: ${dateTimeMinuteFormatter(new Date(accessValidTo))}`
            : '';
         return `Prośba o dostęp do danych ${patientName} została zaakceptowana.${accessValidToText}`;
      }
      case 'REJECTED':
         return `Prośba o dostęp do danych ${patientName} została odrzucona.`;
      case 'REVOKED':
         return `Dostęp do danych ${patientName} został wycofany.`;
      default:
         return `Prośba o dostęp do danych ${patientName}.`;
   }
};

export const getNotificationPath = (notification: Notification): string | undefined => {
   const target = getNotificationTarget(notification);

   switch (target?.type) {
      case 'medicalData':
         return patientPaths.getMedicalDataPath({
            params: { patientId: target.patientId },
            searchParams: { category: target.category, entryId: target.entryId },
         });
      case 'medicalDocument':
         return patientPaths.getMedicalDocumentRepositoryPath({
            params: { patientId: target.patientId },
            searchParams: { documentId: target.documentId },
         });
      case 'visit':
         return visitPaths.getVisitHistoryPath({
            params: { patientId: target.patientId },
            searchParams: { visitId: target.visitId },
         });
      default:
         return undefined;
   }
};
